import { providerRefusedLine } from '@pa/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Isolated } from '../components/ui/Bdi';
import { Check, Close } from '../components/ui/icons';
import { LiveMark } from '../components/ui/LiveMark';
import { titleLabel } from '../lib/activity';
import { api, type TimelineItem } from '../lib/api';
import { relativeWithAbsolute } from '../lib/format';
import { useSession } from '../lib/session';

/**
 * You → Activity (M5.2): what TWO has done today, one real title per row (the label in the UI
 * language, the person's content isolated), the status glyph carrying the meaning, bullets in
 * the line colour, and every relative time with its absolute time in the person's zone.
 */
export function ActivityScreen() {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const locale = i18n.resolvedLanguage ?? 'en';
  const timeZone = me?.timezone ?? 'Asia/Qatar';
  const [items, setItems] = useState<TimelineItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .timeline()
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (items === null) {
    return (
      <div className="mt-4 flex items-center gap-3 py-2" role="status">
        <LiveMark state="thinking" size={16} title={t('common.loading')} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        role="status"
        className="mt-4 rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
      >
        {t('tasks.todayEmpty')}
      </div>
    );
  }
  return (
    <section aria-label={t('tasks.today')} className="mt-4 flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-ink">{t('tasks.today')}</h3>
      <ol className="relative flex flex-col gap-3 border-s border-line ps-5">
        {items.map((item) => {
          const failed = item.status === 'failed';
          return (
            <li key={item.id} className="relative text-sm">
              <span
                aria-hidden
                className="absolute -start-[1.55rem] top-1 flex size-4 items-center justify-center rounded-full border border-line bg-surface"
              >
                {failed ? (
                  <Close size={10} className="text-critical" strokeWidth={2.5} />
                ) : (
                  <Check size={10} className="text-success" strokeWidth={2.5} />
                )}
              </span>
              <p className={['text-ink', failed ? 'text-critical-text' : ''].join(' ')}>
                <span>{titleLabel(item.title, t)}</span>
                {item.title.subject ? (
                  <>
                    <span>: «</span>
                    <bdi dir="auto">
                      <Isolated text={item.title.subject} />
                    </bdi>
                    <span>»</span>
                  </>
                ) : null}
              </p>
              {failed && item.failure ? (
                <p className="text-xs text-critical-text" dir="auto" data-activity-reason>
                  <Isolated
                    text={providerRefusedLine(item.failure, locale === 'ar' ? 'ar' : 'en')}
                  />
                </p>
              ) : null}
              <p className="tabular text-xs text-ink-3">
                {relativeWithAbsolute(item.at, locale, timeZone)}
              </p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
